import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import L from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import {
    LayersControl,
    TileLayer,
    Marker,
    Popup,
    useMap,
    Polyline,
    CircleMarker,
    ScaleControl,
    useMapEvents,
} from 'react-leaflet';
import { Emergency, MarkerIcon } from '../entities';
import { STORE } from '../redux-configs';
import { ACTIONS } from '../actions';
import { scaleMap } from '../../assets/scale-map';
import { EmergencyHelper } from '../helpers';

const backendUrl = process.env.NX_BACKEND_URL,
    backendPort = process.env.NX_BACKEND_PORT;

const goldIcon = `${backendUrl}:${backendPort}/gold-icon.png`,
    redIcon = `${backendUrl}:${backendPort}/red-icon.png`,
    greenIcon = `${backendUrl}:${backendPort}/green-icon.png`,
    blueIcon = `${backendUrl}:${backendPort}/blue-icon.png`,
    iconShadow = `${backendUrl}:${backendPort}/icon-shadow.png`,
    cautionIcon = `${backendUrl}:${backendPort}/caution-icon.png`,
    hospitalIcon = `${backendUrl}:${backendPort}/hospital-icon.png`,
    firstAidIcon = `${backendUrl}:${backendPort}/first-aid-icon.png`,
    cabinIcon = `${backendUrl}:${backendPort}/cabin-icon.png`;

export function MContainer({ location }) {
    const [mapZoom, setMapZoom] = useState(7);

    const dispatch = useDispatch();
    const { selectedEmergency, filteredEmergencies, mapData: { locations, paths } } = useSelector(STORE);
    const map = useMap();

    useMapEvents({
        zoom: () => {
            const zoom = map.getZoom();
            setMapZoom(zoom);
        },
    });

    useEffect(() => {
        const emergency = location ?? selectedEmergency;

        map.setView(
            [
                emergency?.latitude ?? 49.842957,
                emergency?.longitude ?? 24.031111,
            ],
            _.isEmpty(emergency) ? 7 : 16
        );
    }, [map, selectedEmergency, location]);

    const getMarkerColor = (
        emergency: Emergency
    ): { icon: L.Icon; color: string } => {
        let iconUrl, color;
        switch (emergency.status) {
            case 'inProgress': {
                iconUrl = goldIcon;
                color = '#ffd326';
                break;
            }
            case 'finished': {
                if (emergency.finishReason === 'Хибний/тестовий виклик') {
                    iconUrl = blueIcon;
                    color = '#2736b6';
                } else {
                    iconUrl = greenIcon;
                    color = '#25ac22';
                }
                break;
            }
            default: {
                iconUrl = redIcon;
                color = '#ca283b';
                break;
            }
        }
        const markerIcon = new MarkerIcon(
            iconUrl,
            [25, 41],
            iconShadow,
            [12.5, 41]
        );
        const icon = L.icon({ ...markerIcon });
        return { icon, color };
    };

    const getLocationMarker = (location): L.Icon => {
        let iconUrl;
        switch (location.type) {
            case 'Обережність': {
                iconUrl = cautionIcon;
                break;
            }
            case 'Перша медична допомого (Тип-1)': {
                iconUrl = hospitalIcon;
                break;
            }
            case 'Перша медична допомого (Тип-2)': {
                iconUrl = firstAidIcon;
                break;
            }
            default: {
                iconUrl = cabinIcon;
                break;
            }
        }
        const markerIcon = new MarkerIcon(
            iconUrl,
            [32, 37],
            iconShadow,
            [16, 37]
        );
        return L.icon({ ...markerIcon });
    };

    const handleMarkerClick = (event, id) => {
        dispatch({
            type: ACTIONS.SET_SELECTED_EMERGENCY,
            payload: filteredEmergencies.find((e) => e.id === id),
        });
        window.scrollTo(0, 0);
    };

    const multipleLocations = () => {
        return locations?.map((location, index: number) => {
            return (
                <Marker
                    position={location.coordinates}
                    key={index}
                    icon={getLocationMarker(location)}
                >
                    <Popup>
                        <h3>{location.name}</h3>
                        {location.desc && <p>{location.desc}</p>}
                        {location.address && <p>{location.address}</p>}
                    </Popup>
                </Marker>
            );
        });
    }

    return (
        <>
            <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="OpenStreetMap.Mapnik">
                    <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="OpenTopoMap">
                    <TileLayer
                        attribution='&copy; <a href="https://opentopomap.org/">OpenTopoMap</a>, <a href="https://creativecommons.org/licenses/by-sa/3.0/">(CC-BY-SA)</a>, &copy; <a href="http://openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                    />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="BlackAndWhite">
                    <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://tiles.wmflabs.org/bw-mapnik/{z}/{x}/{y}.png"
                    />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="CyclOSM">
                    <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png"
                    />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                    <TileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    />
                </LayersControl.BaseLayer>
            </LayersControl>
            {paths && paths.map((p, index) => {
                const { color, coordinates, name } = p;
                return (
                    <Polyline
                        positions={coordinates}
                        key={index}
                        color={`#${color}`}
                    >
                        <Popup>{name}</Popup>
                    </Polyline>
                );
            })}
            {(location ? [location] : filteredEmergencies).map((emergency: Emergency, index: number) => {
                const marker = getMarkerColor(emergency);
                return (
                    <Marker
                        position={[emergency.latitude, emergency.longitude]}
                        key={index}
                        icon={marker.icon}
                        eventHandlers={{click: (e) => handleMarkerClick(e, emergency.id)}}
                    ><CircleMarker
                        center={[emergency.latitude, emergency.longitude]}
                        radius={(emergency.accuracy / scaleMap[mapZoom]) * 100}
                        color={marker.color}
                    />
                        <Popup>
                            {`Номер телефону: ${emergency.user.phone}`}
                            <br />
                            {`Дата: ${EmergencyHelper.prettifyDate(
                                emergency.createdAt
                            )}`}
                            {emergency.state && (
                                <>
                                    <br />
                                    {`Область: ${emergency?.state}`}
                                </>
                            )}
                            <br />
                            {`Широта: ${emergency.latitude}`}
                            <br />
                            {`Довгота: ${emergency.longitude}`}
                            <br />
                            {`Заряд батареї: ${emergency.battery}`}
                        </Popup>
                    </Marker>
                );
            })}
            {multipleLocations()}
            <ScaleControl position="bottomleft" metric imperial={false} />
        </>
    );
}
